// 📄 pages/Dashboard/menuConfig.tsx
// Fuente única del menú lateral del proyecto (ver ProjectSidebar.tsx) —
// antes esto vivía hardcodeado botón por botón dentro de HomeCA.tsx. 5
// secciones fijas (barra de íconos) + los items de cada una. Los items de
// "Soluciones" y los canales de integración de las demás soluciones (no
// Commerce) siguen viniendo de solucionesConfig.ts/integrationChannels.ts
// (esos SÍ son fuente única de esa data) — acá solo se los referencia para
// poder resolver `seccionDeRuta`, no se duplican.
import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  FileText, ShieldCheck, Cpu, Settings, Layers, Truck, FileCheck, PackageCheck,
  ClipboardList, Database, Coins, Users, Pencil, Info, AlertCircle, Clock, Code2,
  DatabaseZap, Terminal, Bookmark, Receipt, LayoutGrid, Wrench, ArrowLeftRight, KeyRound,
  CalendarDays, FlaskConical, FileCode2, Table2, Network, Flag, GitPullRequest, Rocket, GraduationCap, LifeBuoy
} from "lucide-react";
import { OTRAS_SOLUCIONES } from "../Proyectos/solucionesConfig.ts";
import { CANALES_INTEGRACION } from "../Proyectos/integrationChannels.ts";

// El ícono de un item puede ser un componente lucide (la mayoría) o un
// emoji string (los de "Soluciones", ver solucionesConfig.ts) — mismo
// criterio que ya usaba HomeCA.tsx para esos botones.
export type MenuIcon = LucideIcon | string;

export interface MenuItemDef {
  id: string; // segmento de ruta bajo /proyecto/:projectId/ (matchea activeSection)
  label: string;
  icon: MenuIcon;
  soloConsultor?: boolean;
  // Solo la usan las vistas en grid (HerramientasHome.tsx) — el sidebar no
  // tiene espacio para ella, ver ProjectSidebar.tsx.
  tagline?: string;
  // Sub-fase K7: item del modelo ABAP/IDoc clásico, exclusivo de
  // On-Premise (Cloud usa Communication Arrangements en su lugar, ver
  // ArrangementsSection.tsx). Solo lo usan los 9 canales de
  // CANALES_COMMERCE_ITEMS (NO "integracion/generales", que sigue
  // aplicando a ambos hasta que exista K8).
  onPremOnly?: boolean;
  // Solo en proyectos con el módulo SAP Ariba (ver modulosConfig.ts y
  // plan-proyectos-generales.md). Lo demás es "Gestión del proyecto".
  soloSap?: boolean;
  // Solo si el proyecto tiene la mesa de ayuda activa (`recibeTickets`) o ya
  // tiene tickets (plan-tickets-proyecto.md).
  soloMesaAyuda?: boolean;
}

// Renderiza un MenuIcon (componente lucide o emoji string) al tamaño
// pedido — usado por el sidebar (ProjectSidebar.tsx) y por las vistas en
// grid que consumen estos mismos MenuItemDef (HerramientasHome.tsx), para
// no tener dos copias del mismo switch typeof.
export function IconoDe({ icon, size }: { icon: MenuIcon; size: number }) {
  if (typeof icon === "string") {
    return <span className="leading-none" style={{ fontSize: size * 0.85, width: size, textAlign: "center", display: "inline-block", flexShrink: 0 }}>{icon}</span>;
  }
  const Icon = icon;
  return <Icon size={size} />;
}

export interface SeccionMeta {
  id: string;
  label: string;
  labelCorta: string; // etiqueta chica bajo el ícono en la barra
  icon: LucideIcon;
}

export const SECCIONES_META: SeccionMeta[] = [
  { id: "soluciones", label: "Soluciones del Proyecto", labelCorta: "Soluciones", icon: LayoutGrid },
  { id: "funcionalidades", label: "Funcionalidades", labelCorta: "Funciones", icon: FileText },
  { id: "herramientas", label: "Herramientas", labelCorta: "Herram.", icon: Wrench },
  { id: "proyecto", label: "Proyecto", labelCorta: "Proyecto", icon: Info },
  { id: "configuracion", label: "Configuración", labelCorta: "Config.", icon: Settings }
];

// Funcionalidades: depende de si Flow Extension API está habilitado (flag
// dinámico, ver CommerceHome.tsx) y de qué soluciones están activas en el
// proyecto — "Envío Estatus Facturas"/"Validación XML SUNAT" son propias
// de Commerce Automation (facturación electrónica) y "Buscar Proveedores"
// (SLP, 2026-09-05) de SLP — por eso es una función y no un array.
// Ambos flags de solución default `true` para los consumidores que no
// tienen el proyecto a mano (ver searchIndex.ts, índice global de
// búsqueda: ahí interesa que el item exista siempre como concepto
// encontrable, no filtrado por proyecto) — mismo default que usa el resto
// del código para `solucionesActivas` ausente (`project.solucionesActivas
// || ["commerce"]`, ver ProjectSidebar.tsx).
export const funcionalidadesItems = (flowHabilitado: boolean, slpActivo: boolean = true, commerceActivo: boolean = true): MenuItemDef[] => [
  ...(commerceActivo ? [{ id: "status", label: "Envío Estatus Facturas", icon: FileText, soloSap: true }] : []),
  ...(commerceActivo ? [{ id: "validacion", label: "Validación XML SUNAT", icon: ShieldCheck, soloSap: true }] : []),
  ...(commerceActivo ? [{ id: "facturas", label: "Buscar Facturas", icon: Receipt, soloSap: true }] : []),
  { id: "compXML", label: "Comparar XML", icon: ShieldCheck, soloSap: true },
  { id: "pruebas", label: "Casos de Prueba", icon: FlaskConical },
  { id: "especificaciones", label: "Especificaciones Técnicas", icon: FileCode2 },
  ...(slpActivo ? [{ id: "proveedores", label: "Buscar Proveedores", icon: Users, soloSap: true }] : []),
  ...(flowHabilitado ? [{ id: "flow", label: "Flow Extension API", icon: Cpu, soloSap: true }] : [])
];

// Fuente única de las 6 herramientas del proyecto — antes el sidebar
// (acá) y el grid (HerramientasHome.tsx) tenían cada uno su propia lista a
// mano y se habían desincronizado (el grid tenía "Mi Espacio"/"Configuración"
// en vez de "Interfaces / Canales"/"Credenciales"). Ahora HerramientasHome.tsx
// consume este mismo array — un solo lugar para agregar/quitar una herramienta.
export const HERRAMIENTAS_ITEMS: MenuItemDef[] = [
  {
    id: "validador-estructura", soloSap: true, label: "Validador de Estructura", icon: ShieldCheck,
    tagline: "Compara la estructura de un XML contra una plantilla de referencia."
  },
  {
    id: "apis-test", soloSap: true, label: "Pruebas APIs", icon: Code2,
    tagline: "Explorador y pruebas de APIs de Ariba, S/4HANA y Docusign."
  },
  {
    id: "badis", soloSap: true, label: "BAdIs y Objetos ABAP", icon: DatabaseZap,
    tagline: "Repositorio de BAdIs estándar, programas e interfaces ABAP para SAP."
  },
  {
    id: "transacciones", soloSap: true, label: "Transacciones SAP", icon: Terminal,
    tagline: "Guía y diccionario rápido de transacciones de desarrollo, monitoreo y compras en SAP."
  },
  {
    id: "interfaces", soloSap: true, label: "Interfaces / Canales", icon: ArrowLeftRight,
    tagline: "Catálogo de interfaces y canales de integración disponibles para el proyecto."
  },
  {
    id: "programas", soloSap: true, label: "Programas", icon: Code2,
    tagline: "Catálogo de programas e includes ABAP, relacionados con BAdIs y Transacciones."
  },
  {
    id: "tablas", soloSap: true, label: "Tablas", icon: Table2,
    tagline: "Catálogo de tablas SAP (ej. vía SE16N) con sus campos de referencia."
  },
  // soloConsultor: el rol "cliente" queda afuera por completo (ni ve el
  // ítem en el sidebar) — mismo criterio que esMiembroDelProyecto() del
  // lado de credentialManager.js y las firestore.rules de esta subcolección.
  {
    id: "credenciales", label: "Credenciales", icon: KeyRound, soloConsultor: true,
    tagline: "Vault de credenciales técnicas del proyecto: proveedores, APIs, SAP GUI, Cloud Connector."
  }
];

/** Quita los ítems del módulo SAP Ariba si el proyecto no lo tiene. */
export const segunModulos = (items: MenuItemDef[], sap: boolean): MenuItemDef[] => (sap ? items : items.filter((i) => !i.soloSap));

export const PROYECTO_ITEMS: MenuItemDef[] = [
  { id: "detalle", label: "Detalle del Proyecto", icon: Info },
  // Hitos y avance (plan-proyectos-generales, G4): de Gestión del proyecto, también en SAP.
  { id: "hitos", label: "Hitos y avance", icon: Flag },
  // Solicitudes de cambio (G5): las crea cualquier miembro, también el cliente.
  { id: "cambios", label: "Solicitudes de cambio", icon: GitPullRequest },
  // Versiones (G6): entregas con sus cambios y tareas, y notas de versión.
  { id: "versiones", label: "Versiones", icon: Rocket },
  // Aprendizaje (G7): guías para usuarios finales; el cliente ve solo las publicadas.
  { id: "aprendizaje", label: "Aprendizaje", icon: GraduationCap },
  { id: "incidentes", label: "Incidentes", icon: AlertCircle },
  // Mesa de ayuda (plan-tickets-proyecto.md): tickets del día a día del
  // cliente, distintos de los Incidentes de la implementación.
  { id: "mesa-ayuda", label: "Mesa de ayuda", icon: LifeBuoy, soloMesaAyuda: true },
  { id: "tareas", label: "Tareas", icon: ClipboardList },
  { id: "horas", label: "Mis Horas", icon: Clock },
  { id: "miembros", label: "Miembros", icon: Users },
  { id: "organigrama", label: "Organigrama", icon: Network },
  { id: "ausencias", label: "Ausencias del Equipo", icon: CalendarDays },
  { id: "mi-espacio", label: "Mi Espacio", icon: Bookmark }
];

// Ambos items dependen de esCliente — si el usuario es cliente, esta
// sección queda sin items visibles y su ícono no se muestra en la barra
// (ver itemsVisibles en ProjectSidebar.tsx).
export const CONFIGURACION_ITEMS: MenuItemDef[] = [
  { id: "config", label: "Configuración", icon: Settings, soloConsultor: true, soloSap: true },
  { id: "editar", label: "Editar Proyecto", icon: Pencil, soloConsultor: true }
];

// Canales de Integración de Commerce — antes reemplazaban a
// "Funcionalidades" en el mismo slot del sidebar cuando la ruta activa era
// "integracion/{canal}"; ahora viven anidados dentro del panel de
// "Soluciones" (ver isIntegrationSection en ProjectSidebar.tsx), porque el
// nuevo diseño fija Funcionalidades como uno de los 5 íconos permanentes.
export const CANALES_COMMERCE_ITEMS: MenuItemDef[] = [
  { id: "integracion/generales", label: "Configuraciones Generales", icon: Layers },
  { id: "integracion/oc", label: "Orden de Compra", icon: FileText, onPremOnly: true },
  { id: "integracion/expedicion", label: "Aviso de Expedición", icon: Truck, onPremOnly: true },
  { id: "integracion/confirmacion", label: "Confirmación", icon: FileCheck, onPremOnly: true },
  { id: "integracion/recepcion", label: "Recepción de Mercancía", icon: PackageCheck, onPremOnly: true },
  { id: "integracion/hes-ariba", label: "HES", icon: ClipboardList, onPremOnly: true },
  { id: "integracion/hes-erp", label: "HES - ERP", icon: Database, onPremOnly: true },
  { id: "integracion/facturas", label: "Facturas", icon: Coins, onPremOnly: true },
  { id: "integracion/estatus-facturas", label: "Estatus de Facturas", icon: Receipt, onPremOnly: true },
  { id: "integracion/cutover", label: "Cutover", icon: ClipboardList, onPremOnly: true }
];

const FUNCIONALIDADES_IDS = ["status", "validacion", "facturas", "compXML", "pruebas", "especificaciones", "proveedores", "flow"];
const HERRAMIENTAS_IDS = HERRAMIENTAS_ITEMS.map((i) => i.id);
const PROYECTO_IDS = PROYECTO_ITEMS.map((i) => i.id);
const CONFIGURACION_IDS = CONFIGURACION_ITEMS.map((i) => i.id);
const CANALES_COMMERCE_IDS = CANALES_COMMERCE_ITEMS.map((i) => i.id);
const OTRAS_SOLUCIONES_PATHS = OTRAS_SOLUCIONES.map((s) => s.path);
// Canales de las demás soluciones (SLP/Sourcing/Contracts) no tienen un
// link propio en el sidebar (se navega desde adentro de la solución, ver
// CanalIntegracionView.tsx) pero igual deben "activar" el ícono de
// Soluciones si el usuario llega directo a esa ruta.
const CANALES_OTRAS_SOLUCIONES_PATHS = CANALES_INTEGRACION.map((c) => `integracion/${c.solucion}/${c.slug}`);

// Ruta activa (activeSection, ver HomeCA.tsx) → id de sección dueña, o
// null si no pertenece a ninguna (ej. la landing "" o una ruta no
// contemplada). Usado para: 1) resaltar el ícono de la sección activa en
// la barra aunque su panel esté cerrado, 2) auto-abrir el panel correcto
// cuando se llega directo a una ruta (ver ProjectSidebar.tsx).
export const seccionDeRuta = (activeSection: string): string | null => {
  if (!activeSection) return null;
  if (activeSection === "commerce") return "soluciones";
  if (OTRAS_SOLUCIONES_PATHS.includes(activeSection)) return "soluciones";
  if (CANALES_COMMERCE_IDS.includes(activeSection)) return "soluciones";
  if (CANALES_OTRAS_SOLUCIONES_PATHS.includes(activeSection)) return "soluciones";
  if (FUNCIONALIDADES_IDS.includes(activeSection)) return "funcionalidades";
  if (HERRAMIENTAS_IDS.includes(activeSection)) return "herramientas";
  if (PROYECTO_IDS.includes(activeSection)) return "proyecto";
  if (CONFIGURACION_IDS.includes(activeSection)) return "configuracion";
  return null;
};
